import { getSupabaseClient } from '../lib/supabase';
import { EVENT_COLUMNS } from './events';
import type {
  WebEvent,
  WebEventExpireAction,
  WebPhotoRemovePolicy,
} from '../types/events';
import type { WebEventPermissionSummary } from '../types/people';

export type HostEventDetailsPatch = {
  name: string;
  description: string | null;
  start_time: string | null;
  end_time: string | null;
};

export type HostAccessSettingsPatch = {
  expires_at: string | null;
  expire_action: WebEventExpireAction;
  photo_remove_policy: WebPhotoRemovePolicy;
};

type HostBooleanField = 'is_public' | 'allow_guest_downloads' | 'auto_upload_enabled';
type HostLifecycleTarget = 'active' | 'archived' | 'deleted';

function normalizePermission(data: unknown): WebEventPermissionSummary | null {
  const value = Array.isArray(data) ? data[0] : data;
  return value && typeof value === 'object' ? (value as WebEventPermissionSummary) : null;
}

export function canManageHostSettings(
  permission: WebEventPermissionSummary | null | undefined,
): boolean {
  return Boolean(permission?.canManageSettings || permission?.isOwner || permission?.isAppAdmin);
}

export function canUseHostDangerZone(
  permission: WebEventPermissionSummary | null | undefined,
): boolean {
  return Boolean(permission?.isOwner || permission?.isAppAdmin);
}

export async function loadHostControlPermission(
  eventId: string,
): Promise<WebEventPermissionSummary | null> {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('get_my_event_permission_summary', {
    p_event_id: eventId,
  });
  if (error) throw error;
  return normalizePermission(data);
}

async function assertSettingsAccess(eventId: string) {
  const permission = await loadHostControlPermission(eventId);
  if (!canManageHostSettings(permission)) {
    throw new Error('You do not have permission to change this event’s settings.');
  }
  return permission;
}

async function assertDangerAccess(eventId: string) {
  const permission = await loadHostControlPermission(eventId);
  if (!canUseHostDangerZone(permission)) {
    throw new Error('Only the event owner can archive, restore, end, or delete this event.');
  }
  return permission;
}

async function loadUpdatedEvent(eventId: string): Promise<Partial<WebEvent>> {
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('events')
    .select(EVENT_COLUMNS)
    .eq('id', eventId)
    .maybeSingle();
  if (error) throw error;
  return (data ?? {}) as Partial<WebEvent>;
}

export async function updateHostEventDetails(
  eventId: string,
  patch: HostEventDetailsPatch,
): Promise<Partial<WebEvent>> {
  await assertSettingsAccess(eventId);
  const name = patch.name.trim();
  if (!name) throw new Error('Event name is required.');

  const client = getSupabaseClient();
  const { error } = await client
    .from('events')
    .update({
      name,
      description: patch.description?.trim() || null,
      start_time: patch.start_time,
      end_time: patch.end_time,
    })
    .eq('id', eventId);
  if (error) throw error;
  return loadUpdatedEvent(eventId);
}

export async function updateHostBooleanSetting(
  eventId: string,
  field: HostBooleanField,
  value: boolean,
): Promise<Partial<WebEvent>> {
  await assertSettingsAccess(eventId);
  const client = getSupabaseClient();
  const { error } = await client.from('events').update({ [field]: value }).eq('id', eventId);
  if (error) throw error;
  return loadUpdatedEvent(eventId);
}

export async function saveHostAccessSettings(
  eventId: string,
  patch: HostAccessSettingsPatch,
): Promise<Partial<WebEvent>> {
  await assertSettingsAccess(eventId);
  const client = getSupabaseClient();
  const { error } = await client
    .from('events')
    .update({
      expires_at: patch.expires_at,
      expire_action: patch.expire_action,
      photo_remove_policy: patch.photo_remove_policy,
    })
    .eq('id', eventId);
  if (error) throw error;
  return loadUpdatedEvent(eventId);
}

export async function setHostUploadsLocked(
  eventId: string,
  value: boolean,
): Promise<Partial<WebEvent>> {
  await assertSettingsAccess(eventId);
  const client = getSupabaseClient();
  const { error } = await client.from('events').update({ uploads_locked: value }).eq('id', eventId);
  if (error) throw error;
  return loadUpdatedEvent(eventId);
}

export async function transitionHostEventLifecycle(params: {
  eventId: string;
  toState: HostLifecycleTarget;
  reason: string;
  source: string;
}): Promise<Partial<WebEvent>> {
  await assertDangerAccess(params.eventId);
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('app_transition_event_lifecycle', {
    p_event_id: params.eventId,
    p_to_state: params.toState,
    p_reason: params.reason,
    p_metadata: {
      source: params.source,
      client: 'web',
      phase: 'web-parity-p5',
    },
  });
  if (error) throw error;

  if (params.toState === 'deleted') {
    const transitioned = (Array.isArray(data) ? data[0] : data) as Partial<WebEvent> | null;
    return {
      ...(transitioned ?? {}),
      id: params.eventId,
      lifecycle_state: 'deleted',
      storage_status: 'deleted',
      deleted_at: transitioned?.deleted_at ?? new Date().toISOString(),
    };
  }

  return loadUpdatedEvent(params.eventId);
}
